/**
 * Five Point PDP (layout v7a) — behaviour for `sections/fivepoint-pdp.liquid`.
 *
 * Ported from the approved prototype `prototypes/2026-08-27-pdp-v7/`, with one
 * structural change: the prototype rebuilt its choice surface with innerHTML on
 * every state change. Here every element is server-rendered by Liquid and this
 * module only toggles attributes and text, so Liquid stays the single source of
 * truth for markup and the page is usable (photographs, copy, specification,
 * colour range) before this module executes.
 *
 * What is native Horizon and NOT re-implemented here:
 *   - add to cart            -> <product-form-component> + <add-to-cart-component>
 *   - cart drawer / cart UI  -> Horizon's own, unchanged
 *   - full-screen photograph -> <zoom-dialog>, opened declaratively via on:click
 *   - money formatting       -> @theme/money-formatting
 *   - the drawer itself      -> a native <dialog> (Esc, focus trap, ::backdrop)
 *
 * ...and, since pdp-gallery-v9b (2026-09-07), the GESTURE as well. The band
 * below the fold is a CSS scroll-snap track, so the swipe, its momentum, its
 * rubber-band and its direction are the browser's. This module reads the track's
 * scroll position to know which photograph is showing, and writes `scrollLeft`
 * when a thumbnail, an arrow or an arrow key asks for another one. There is no
 * touch handler, no threshold and no drag maths anywhere in this file: the
 * earlier version measured `touchstart`/`touchend` deltas on the stage itself,
 * which is the invented interaction the owner asked to be taken out.
 *
 * ONE INDEX, ONE VIEWER. `#image` is the single piece of state: the cover reel,
 * the plate, the stage dots, the `n of N` caption, the pressed thumbnail, the
 * counter and the band carousel's scroll position are all written from it, and
 * every ⤢ affordance opens Horizon's `<zoom-dialog>` at it.
 *
 * v9c: there are now TWO scroll-snap tracks — the cover reel at the top of the
 * page and the carousel in the band — and either can be swiped. They mirror each
 * other through `#image`: whichever one the finger moved is READ, the other is
 * written. A short lock covers programmatic smooth scrolls, because a scroll
 * animation fires events all the way along and a listener that answered them
 * would fight the animation it was watching.
 *
 * What this module owns:
 *   - which photograph is current, and everything that reports it
 *   - which purchase form is chosen, which colour, how many metres
 *   - keeping the native form's hidden `id` and `quantity` inputs in sync
 *
 * NOTHING ON THIS PAGE MOVES BY ITSELF. An earlier version advanced the phone
 * photograph on a timer until the first interaction. The frozen brief's non-goal
 * "No carousel autoplay" is a statement about the finished page, not only about
 * newly written code, so the timer is gone: the photograph changes when a
 * visitor swipes it, presses a thumbnail or presses an arrow, and at no other
 * time. The runtime harness asserts that connecting the element schedules no
 * interval at all.
 */

import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const DESKTOP_QUERY = '(min-width: 1024px)';

class FivepointPdp extends Component {
  /** @type {any} */
  #data = { colours: [], swatch: null, strings: {}, maxQty: 99 };
  #form = 'swatch';
  /** @type {string|null} */
  #colour = null;
  #qty = 1;
  #image = 0;

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.querySelector('[data-fp-data]');
    if (dataEl) {
      try {
        this.#data = JSON.parse(dataEl.textContent || '{}');
      } catch (error) {
        console.warn('[fivepoint-pdp] could not read section data', error);
      }
    }

    // The drawer must open on something a visitor can actually buy. A colour card
    // that exists but is unavailable is a dead default, so availability — not mere
    // existence — decides. Liquid computes the same thing for the server-rendered
    // selection; this keeps the two in step.
    const buyableColours = this.#colourList().filter((c) => c.available);
    if (this.dataset.pdpLayout === 'catalogue' && buyableColours.length) this.#form = 'sample';
    else if (this.#data.swatch && this.#data.swatch.available) this.#form = 'swatch';
    else if (buyableColours.length) this.#form = 'sample';
    else this.#form = 'bulk';
    if (buyableColours.length === 1) this.#colour = buyableColours[0].n;

    this.#mountRail();
    this.#desktopQuery.addEventListener('change', this.#mountRail);

    // pdp-v10-B r3: the video cells and their viewer items exist only at desktop
    // widths, and only as <template> content until this runs.
    this.#mountDesktopVideos();
    this.#desktopQuery.addEventListener('change', this.#mountDesktopVideos);

    // The listeners the two reels need: the browser scrolls a track, these read
    // back which slide it settled on. Passive, because nothing here can cancel
    // the scroll it is watching.
    this.#track()?.addEventListener('scroll', this.#onTrackScroll, { passive: true });
    this.#heroTrack()?.addEventListener('scroll', this.#onHeroScroll, { passive: true });

    // ...and the full-screen viewer, whose thumbnail rail has to follow the
    // photograph the visitor swipes to. Horizon only marks it on open.
    for (const scroller of this.#viewerScrollers()) {
      scroller.addEventListener('scroll', this.#onViewerScroll, { passive: true });
    }

    // ...and the viewer's own close, which has to stop a playing video (r2, M2).
    this.querySelector('zoom-dialog dialog')?.addEventListener('close', this.#onViewerClose);

    // Horizon's cart drawer defers its auto-open while the add came from an open
    // modal <dialog> — it waits for that dialog's native `close`. So closing this
    // sheet on success IS the handoff. On failure it must stay open, with the
    // native error node (ref="addToCartTextError") showing why.
    this.#productForm = this.querySelector('product-form-component');
    this.#productForm?.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#productForm?.addEventListener(StandardEvents.cartError, this.#onCartError);

    const sheet = this.querySelector('[data-fp-sheet]');
    sheet?.addEventListener('click', this.#onSheetClick);

    this.#goTo(0, 'instant');
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#productForm?.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#productForm?.removeEventListener(StandardEvents.cartError, this.#onCartError);
    this.querySelector('[data-fp-sheet]')?.removeEventListener('click', this.#onSheetClick);
    this.#desktopQuery.removeEventListener('change', this.#mountRail);
    this.#desktopQuery.removeEventListener('change', this.#mountDesktopVideos);
    this.#track()?.removeEventListener('scroll', this.#onTrackScroll);
    this.#heroTrack()?.removeEventListener('scroll', this.#onHeroScroll);
    for (const scroller of this.#viewerScrollers()) {
      scroller.removeEventListener('scroll', this.#onViewerScroll);
    }
    this.querySelector('zoom-dialog dialog')?.removeEventListener('close', this.#onViewerClose);
    clearTimeout(this.#scrollLockTimer);
  }

  #desktopQuery = matchMedia(DESKTOP_QUERY);

  /** @type {HTMLElement|null} */
  #productForm = null;

  /** Whether a CartErrorEvent was seen during the attempt in flight. */
  #sawCartError = false;

  /**
   * Round 2 finding 4. Horizon covers only ONE of the three ways an add can
   * fail. Reading the pinned build (`product-form.js`):
   *
   *   business error (422, quantity rule)  -> CartErrorEvent{code:'INVALID'} AND
   *                                           Horizon writes addToCartTextError
   *   fetch / non-JSON response            -> CartErrorEvent{code:'SERVICE_UNAVAILABLE'}
   *                                           and a promise rejection, but NO text
   *                                           written anywhere
   *   cart refresh fails after a good add  -> promise rejection ONLY: no event,
   *                                           no text, and the item may well be
   *                                           in the cart already
   *
   * That last case is why the two are told apart rather than lumped into one
   * "something went wrong": telling someone an add failed when it actually
   * succeeded invites a duplicate order. The presence of a CartErrorEvent for
   * this attempt is the discriminator.
   */
  #onCartError = (event) => {
    this.#sawCartError = true;
    // Horizon writes the text itself for INVALID; for anything else it only
    // dispatches, so the message would otherwise never reach the visitor.
    if (event.code !== 'INVALID') {
      this.#showCartError(this.#string('cartErrorAdd'));
    }
  };

  #onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;
    this.#sawCartError = false;
    this.#clearCartError();

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return; // Horizon has surfaced it; keep the sheet open
        this.closeSheet();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.warn('[fivepoint-pdp] add to cart rejected', error);
        // No CartErrorEvent for this attempt means the request itself got
        // through and only the follow-up cart read failed — so do not tell the
        // visitor the add failed.
        this.#showCartError(this.#string(this.#sawCartError ? 'cartErrorAdd' : 'cartErrorRefresh'));
      });
  };

  #string(key) {
    return (this.#data.strings || {})[key] || '';
  }

  /**
   * Populates BOTH sinks Horizon uses: the visible `addToCartTextError` node and
   * the `liveRegion` a screen reader is listening to. Writing only one of them
   * leaves half the audience with no idea what happened.
   */
  #showCartError(message) {
    if (!message) return;
    const form = this.#productForm;
    const errorNode = form?.refs?.addToCartTextError;
    if (errorNode) {
      errorNode.classList.remove('hidden');
      const existing = Array.from(errorNode.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      if (existing) existing.textContent = message;
      else errorNode.appendChild(document.createTextNode(message));
    }
    const liveRegion = form?.refs?.liveRegion;
    if (liveRegion) {
      liveRegion.textContent = '';
      // A same-tick rewrite is not re-announced; yield first.
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 50);
    }
  }

  #clearCartError() {
    const errorNode = this.#productForm?.refs?.addToCartTextError;
    if (!errorNode) return;
    errorNode.classList.add('hidden');
    for (const node of Array.from(errorNode.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
    }
  }

  /**
   * A native <dialog> does not close on a backdrop press by itself. Only a press
   * that lands on the dialog element itself is the backdrop — a press inside the
   * panel has a nearer target, so real content never closes the sheet by accident.
   */
  #onSheetClick = (event) => {
    if (event.target === event.currentTarget) this.closeSheet();
  };

  #colourList() {
    return Array.isArray(this.#data.colours) ? this.#data.colours : [];
  }

  #currentColour() {
    return this.#colourList().find((c) => String(c.n) === String(this.#colour)) || null;
  }

  /**
   * D34. The "Other" colour is a real variant like any other sample colour, and
   * it is Liquid — not this module — that decides which entry it is: the flag
   * comes down in the section's own data, so no string here has to agree with a
   * SKU shape.
   * @param {any} colour
   */
  #isOther(colour) {
    return Boolean(colour && colour.other);
  }

  /** Liquid validates named-colour SKU/label identity; the module only renders that verdict. */
  #isNamed(colour) {
    return Boolean(colour && colour.named);
  }

  #otherInput() {
    return this.querySelector('[data-fp-other-input]');
  }

  /** What the shopper has written, with surrounding whitespace ignored. */
  #otherText() {
    const input = this.#otherInput();
    return input ? String(input.value || '').trim() : '';
  }

  /**
   * The Other colour cannot be ordered until that line is written, so every
   * keystroke can change whether this page has anything to submit. Declarative
   * `on:input`: Horizon delegates `input` at the document, so the field does not
   * have to exist when this element connects.
   */
  refreshOther() {
    this.render();
  }

  #money(cents) {
    const format = this.#data.moneyFormat || '${{amount}}';
    const currency = this.#data.currency || 'USD';
    return formatMoney(cents, format, currency);
  }

  /* ---------------- the photographs ---------------- */

  /**
   * The rail is a single element with two mount points: inside the reading
   * column on the desktop, and under the carousel below the fold on phone and
   * tablet. Moving it (rather than rendering it twice) keeps one pressed state —
   * and keeps the count the runtime harness asserts honest.
   */
  #mountRail = () => {
    const rail = this.querySelector('[data-fp-rail]');
    const target = this.querySelector('[data-fp-rail-commerce]') || (this.#desktopQuery.matches
      ? this.querySelector('[data-fp-rail-desk]')
      : this.querySelector('[data-fp-rail-page]'));
    if (rail && target && rail.parentElement !== target) target.appendChild(rail);
  };

  /**
   * pdp-v10-B r3 — the desktop-only video surface.
   *
   * The section ships the video cells and their viewer items inside two
   * `<template>` elements and nowhere else. A `<template>`'s content is inert:
   * it is not rendered, has no box, holds no text, takes no Tab stop and is not
   * in the accessibility tree. So on a phone the video markup is not in the
   * document, and the page is the live theme's page — which is the whole point,
   * and what the 390px comparison in `.agent/reviews/pdp-v10-b/r3/` measures.
   *
   * At 1024px and up this clones the cells onto the END of the grid track and
   * the media items onto the END of the viewer's list, and below that width it
   * removes them again. Both directions matter: this runs on every change of the
   * media query, not only on connect, so a window dragged narrow does not leave
   * a video behind.
   *
   * The cells are appended, never re-ordered, so DOM order is the reading order
   * is the visual order (r2/M1). There is no CSS `order` anywhere in this slice.
   */
  #mountDesktopVideos = () => {
    const cells = this.querySelector('template[data-fp-video-cells]');
    const media = this.querySelector('template[data-fp-video-media]');
    if (!cells || !media) return;

    const mounted = this.querySelectorAll('[data-fp-video-cell]').length > 0;
    if (!this.#desktopQuery.matches) {
      if (!mounted) return;
      for (const node of this.querySelectorAll('[data-fp-video-cell], [data-fp-video-item]')) {
        // A video that is playing when the window narrows is stopped rather than
        // detached mid-play: a detached <video> keeps its audio in some engines.
        for (const video of node.querySelectorAll('video')) video.pause();
        node.remove();
      }
      return;
    }
    if (mounted) return;

    const track = this.#track();
    const list = this.querySelector('.dialog-zoomed-gallery');
    if (!track || !list) return;
    track.append(cells.content.cloneNode(true));
    list.append(media.content.cloneNode(true));

    // The cloned cells are the only thing on the page that opens the viewer on a
    // video, so their handler is wired here rather than declaratively: Horizon's
    // `on:` bindings are read when the component connects, and these nodes did
    // not exist then.
    for (const button of this.querySelectorAll('[data-fp-video-open]')) {
      button.addEventListener('click', this.#onVideoCellClick);
    }
  };

  /** How many PHOTOGRAPHS the viewer holds — the offset every video item sits after. */
  #photoCount() {
    return this.querySelectorAll('.dialog-zoomed-gallery > li:not([data-fp-video-item])').length;
  }

  /**
   * A video cell was pressed. The viewer is Horizon's `<zoom-dialog>`, whose
   * `open(index)` knows only the items that were in the list when it connected —
   * the photographs. So the dialog is opened on the last photograph and the
   * video is then scrolled into view in the same list, which is a scroll-snap
   * container and is exactly how a swipe moves between items anyway. From there
   * the existing scroll handler owns everything: the ring, the pausing, the page
   * behind the dialog.
   *
   * @param {MouseEvent} event
   */
  #onVideoCellClick = (event) => {
    const button = event.currentTarget;
    const offset = Number(button.dataset.fpVideoOpen || 0);
    const photos = this.#photoCount();
    this.#openViewerAt(Math.max(0, photos - 1), event);
    const item = this.querySelector(`[data-fp-video-item="${offset}"]`);
    if (!item) return;
    // After the dialog's own opening work, or the scroll is overwritten by it.
    requestAnimationFrame(() => {
      try {
        item.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
      } catch {
        item.scrollIntoView();
      }
      this.#paintViewer(photos + offset);
    });
  };

  /** The band's scroll-snap track. Absent on a product with no photographs. */
  #track() {
    return this.querySelector('[data-fp-track]');
  }

  /** The cover reel at the top of the page. */
  #heroTrack() {
    return this.querySelector('[data-fp-hero-track]');
  }

  /** @returns {HTMLElement[]} the band's slides, in document order */
  #slides() {
    return Array.from(this.querySelectorAll('[data-fp-slide]'));
  }

  /** The elements the open viewer scrolls: the list on a phone, the dialog above it. */
  #viewerScrollers() {
    return Array.from(this.querySelectorAll('zoom-dialog dialog, zoom-dialog .dialog-zoomed-gallery'));
  }

  /**
   * A programmatic smooth scroll fires `scroll` all the way along, and a
   * listener that answered those events would keep re-deciding which slide is
   * current while the animation was still travelling — and, worse, write the
   * other track to that half-way answer. So a move announces itself and the
   * listeners stand down until it has landed. Nothing here is a timer that moves
   * the page: it only decides whether to believe a scroll position.
   *
   * @param {number} ms
   */
  #lockScroll(ms) {
    this.#scrollLock = true;
    clearTimeout(this.#scrollLockTimer);
    this.#scrollLockTimer = setTimeout(() => {
      this.#scrollLock = false;
    }, ms);
  }

  #scrollLock = false;
  #scrollLockTimer = 0;

  /**
   * Scroll `container` so that its `selector` child at `index` is in view.
   * @param {Element|null} container
   * @param {string} selector
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollReel(container, selector, index, behavior) {
    if (!container) return;
    const slide = container.querySelectorAll(selector)[index];
    if (!slide) return;
    const left = slide.offsetLeft - container.offsetLeft;
    // `scrollTo` with a behaviour where it exists; a plain assignment where it
    // does not (older engines, and jsdom, which the runtime harness runs in).
    try {
      if (typeof container.scrollTo === 'function') container.scrollTo({ left, behavior });
      else container.scrollLeft = left;
    } catch {
      container.scrollLeft = left;
    }
  }

  /**
   * Which child of `container` sits under its own middle.
   * @param {Element|null} container
   * @param {string} selector
   * @returns {number}
   */
  #nearestSlide(container, selector) {
    if (!container) return -1;
    const slides = Array.from(container.querySelectorAll(selector));
    if (!slides.length) return -1;
    const middle = container.scrollLeft + container.clientWidth / 2;
    let nearest = 0;
    let shortest = Infinity;
    slides.forEach((slide, i) => {
      const centre = slide.offsetLeft - container.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(centre - middle);
      if (distance < shortest) {
        shortest = distance;
        nearest = i;
      }
    });
    return nearest;
  }

  /**
   * Make photograph `index` the current one, everywhere.
   *
   * The index is CLAMPED, not wrapped. A scroll-snap track has a first and a
   * last slide that a finger cannot swipe past, so an arrow that jumped from the
   * last photograph back to the first would be a behaviour the gesture does not
   * have — and the arrows disable at the ends for the same reason.
   *
   * Declarative bindings call methods as `method(data, event)` — Horizon appends
   * the event to every action — so the scroll behaviour is NOT a second
   * parameter here. `#goTo` takes it; this is the public one-argument door.
   *
   * @param {number|string} index
   */
  showImage(index) {
    this.#goTo(index, 'smooth');
  }

  /**
   * Adopt `index` as the current photograph WITHOUT touching the track's scroll
   * position: the hero cover, the plate, and everything that reports the state.
   *
   * This is deliberately separate from `#goTo`. The scroll listener runs while a
   * smooth scroll is still in flight, and an earlier draft had it call `#goTo`,
   * which wrote `scrollLeft` back to whichever slide was nearest at that instant
   * — cancelling the very scroll that was moving. Measured on the preview: an
   * arrow key or an arrow press after a swipe did nothing at all. Only the
   * public movers write scroll; the listener only reads it.
   *
   * @param {number|string} index
   */
  #setCurrent(index) {
    const thumbs = Array.from(this.querySelectorAll('[data-fp-thumb]'));
    if (!thumbs.length) return false;

    const wanted = Number(index);
    this.#image = Math.min(Math.max(Number.isFinite(wanted) ? wanted : 0, 0), thumbs.length - 1);

    // layout a's contained plate is still a single image, so it is swapped
    const thumb = thumbs[this.#image];
    const src = thumb.dataset.src;
    const alt = thumb.dataset.alt || '';
    for (const target of this.querySelectorAll('[data-fp-plate-img]')) {
      if (src) target.src = src;
      target.alt = alt;
    }

    this.#paintGallery();
    return true;
  }

  /**
   * @param {number|string} index
   * @param {ScrollBehavior} behavior
   */
  #goTo(index, behavior) {
    if (!this.#setCurrent(index)) return;
    if (behavior === 'smooth') this.#lockScroll(600);
    this.#scrollReel(this.#heroTrack(), '[data-fp-hero-slide]', this.#image, behavior);
    this.#scrollReel(this.#track(), '[data-fp-slide]', this.#image, behavior);
  }

  /** Move one photograph on. Bound declaratively to the next arrow. */
  nextImage() {
    this.showImage(this.#image + 1);
  }

  /** Move one photograph back. Bound declaratively to the previous arrow. */
  prevImage() {
    this.showImage(this.#image - 1);
  }

  /**
   * A thumbnail. It makes that photograph current — the hero, the dots and the
   * carousel all follow — and opens the full-screen viewer on it, which is what
   * the owner asked a thumbnail press to do wherever the rail is mounted.
   *
   * @param {number|string} index
   * @param {Event} event
   */
  openImage(index, event) {
    this.showImage(index);
    if (this.dataset.pdpLayout === 'catalogue' && event?.target?.closest('[data-fp-rail-commerce]')) return;
    this.#openViewerAt(this.#image, event);
  }

  /**
   * Every ⤢ affordance: the one on the stage, the one on layout a's plate, and
   * the one in the band's heading. All three open the viewer where the visitor
   * already is rather than at a fixed index.
   *
   * @param {Event} event
   */
  openViewer(event) {
    this.#openViewerAt(this.#image, event);
  }

  /**
   * Horizon's own `<zoom-dialog>` does the opening, the view transition, the
   * thumbnails, Escape and the close. This only tells it which photograph, and
   * hands it the event it needs to suppress the default action.
   *
   * @param {number} index
   * @param {Event} event
   */
  #openViewerAt(index, event) {
    const dialog = this.querySelector('zoom-dialog');
    if (!dialog) return;
    // The element can be in the DOM before its module has run; upgrading is
    // synchronous, idempotent and a no-op when it already happened.
    customElements.upgrade?.(dialog);
    if (typeof dialog.open !== 'function') return;

    // Horizon marks the dialog's own thumbnail rail from inside `open()` only on
    // the view-transition path, and never again afterwards. The ring is ours to
    // write, on open and on every swipe after it.
    this.#paintViewer(index);
    Promise.resolve(dialog.open(index, event))
      .then(() => this.#paintViewer(index))
      .catch(() => {});
  }

  /**
   * The arrow keys on the track, which is a single tab stop (`tabindex="0"`).
   * Left/Right are what a carousel answers to everywhere; Home and End go to the
   * ends. Nothing else is claimed, so Tab still leaves and Enter still opens the
   * photograph that has focus.
   *
   * @param {KeyboardEvent} event
   */
  handleGalleryKeys(event) {
    if (!this.#slides().length) return;
    if (event.key === 'ArrowRight') this.nextImage();
    else if (event.key === 'ArrowLeft') this.prevImage();
    else if (event.key === 'Home') this.showImage(0);
    else if (event.key === 'End') this.showImage(this.#slides().length - 1);
    else return;
    // Only once a key above has been answered, so an unhandled key keeps its
    // native meaning.
    event.preventDefault();
  }

  /**
   * A reel was scrolled. Whichever one the finger moved is READ; the other is
   * written, instantly, so the two can never animate at each other. Read from
   * scroll position rather than from a gesture, so a swipe, a snap-back, a
   * keyboard scroll and a trackpad flick all report through one path.
   *
   * @param {Element|null} source
   * @param {string} sourceSelector
   * @param {Element|null} mirror
   * @param {string} mirrorSelector
   */
  #followReel(source, sourceSelector, mirror, mirrorSelector) {
    if (this.#scrollLock || !source) return;
    const nearest = this.#nearestSlide(source, sourceSelector);
    if (nearest < 0 || nearest === this.#image) return;
    this.#setCurrent(nearest);
    this.#scrollReel(mirror, mirrorSelector, this.#image, 'instant');
  }

  #onTrackScroll = () => {
    this.#followReel(this.#track(), '[data-fp-slide]', this.#heroTrack(), '[data-fp-hero-slide]');
  };

  #onHeroScroll = () => {
    this.#followReel(this.#heroTrack(), '[data-fp-hero-slide]', this.#track(), '[data-fp-slide]');
  };

  /**
   * The full-screen viewer was scrolled. Horizon marks its thumbnail rail from
   * inside `open()` only, so once the visitor swipes to the next photograph the
   * ring is left behind on the one they came in on. This writes it — and only
   * it, plus the rail's own scroll position, never the media scroller — so there
   * is no loop, and it brings the page behind the dialog along so that closing
   * it leaves the visitor on the photograph they were looking at.
   */
  #onViewerScroll = () => {
    const items = Array.from(this.querySelectorAll('.dialog-zoomed-gallery > li'));
    if (!items.length) return;

    const midX = window.innerWidth / 2;
    const midY = window.innerHeight / 2;
    let nearest = -1;
    let shortest = Infinity;
    items.forEach((item, i) => {
      const box = item.getBoundingClientRect();
      const dx = (box.left + box.right) / 2 - midX;
      const dy = (box.top + box.bottom) / 2 - midY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < shortest) {
        shortest = distance;
        nearest = i;
      }
    });
    if (nearest < 0) return;

    this.#paintViewer(nearest);
    // r3: the viewer's list can hold video items after the photographs. They are
    // not photographs, so they must not drive the hero reel, the dots or the
    // band carousel — the page behind the dialog stays on the last photograph
    // the visitor actually looked at.
    if (nearest >= this.#photoCount()) return;
    if (nearest !== this.#image) this.#goTo(nearest, 'instant');
  };

  /**
   * Stop every video in the viewer except the one at `keep`; `null` stops all of
   * them.
   *
   * pdp-v10-B r2, cross-family review M2. The viewer renders a video with
   * Shopify's `video_tag` rather than through Horizon's `<deferred-media>`,
   * because the whole point of the viewer is the browser's own controls. But
   * `deferred-media` is also where Horizon's pause-on-close lives, so bypassing
   * it means a video kept playing — audible — after Escape closed the dialog,
   * and an earlier video kept playing while the visitor swiped to the next
   * photograph. Nothing about `<dialog>` pauses media on its own.
   *
   * This is deliberately a query over the DOM rather than a list held in a
   * field: the items are cloned in and removed again as the window crosses
   * 1024px, and a stale reference to a removed element is how a fix like this
   * rots.
   *
   * @param {number|null} keep
   */
  #pauseViewerVideos(keep = null) {
    const items = Array.from(this.querySelectorAll('.dialog-zoomed-gallery > li'));
    items.forEach((item, i) => {
      if (keep !== null && i === keep) return;
      for (const video of item.querySelectorAll('video')) {
        if (!video.paused) video.pause();
      }
    });
  }

  /**
   * The viewer closed — by Escape, by the close button, by the backdrop, or by a
   * photograph being clicked. `<dialog>` fires `close` for every one of them,
   * which is why the native event is what this listens to rather than Horizon's
   * own `DialogCloseEvent`: that one is dispatched from `closeDialog()` only,
   * and this must hold for any path that ends with the dialog shut.
   */
  #onViewerClose = () => {
    this.#pauseViewerVideos(null);
  };

  /**
   * The viewer's own thumbnail rail: which one is ringed, and keeping it in
   * view. Written directly rather than through Horizon's `selectThumbnail`,
   * which also scrolls the media — from a scroll handler that would be a loop.
   *
   * @param {number} index
   */
  #paintViewer(index) {
    // Whatever else this call does, the media that is NOT on screen stops
    // playing first — before the early return below, which fires on a product
    // whose viewer has no thumbnail rail.
    this.#pauseViewerVideos(index);

    const thumbs = Array.from(this.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
    if (!thumbs.length) return;
    thumbs.forEach((button, i) => button.setAttribute('aria-selected', String(i === index)));

    const active = thumbs[index];
    const rail = active?.parentElement;
    if (!active || !rail || rail.scrollWidth <= rail.clientWidth) return;
    const target = active.offsetLeft - rail.offsetLeft - (rail.clientWidth - active.offsetWidth) / 2;
    const left = Math.min(Math.max(target, 0), rail.scrollWidth - rail.clientWidth);
    try {
      if (typeof rail.scrollTo === 'function') rail.scrollTo({ left, behavior: 'smooth' });
      else rail.scrollLeft = left;
    } catch {
      rail.scrollLeft = left;
    }
  }

  /**
   * Everything that REPORTS which photograph is current, written from the one
   * `#image` value: the stage dots, the `3 / 5` counter, the `Photograph 3 of 5`
   * caption, the pressed thumbnail, and whether an arrow has anywhere to go.
   */
  #paintGallery() {
    const thumbs = Array.from(this.querySelectorAll('[data-fp-thumb]'));
    const total = thumbs.length;
    const here = this.#image;

    thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === here)));

    const active = thumbs[here];
    const caption = this.querySelector('[data-fp-plate-cap]');
    if (caption && active) caption.textContent = active.dataset.caption || '';

    const dots = this.querySelectorAll('[data-fp-dots] i');
    dots.forEach((dot, i) => dot.classList.toggle('is-on', i === here));

    const counter = this.querySelector('[data-fp-counter]');
    if (counter && total) counter.textContent = `${here + 1} / ${total}`;

    this.querySelectorAll('[data-fp-prev]').forEach(button => { button.disabled = here <= 0; });
    this.querySelectorAll('[data-fp-next]').forEach(button => { button.disabled = here >= total - 1; });

    // Keep the pressed thumbnail inside its own rail's scrolled view. The rail
    // is the only thing moved here, never the page.
    const rail = this.querySelector('[data-fp-rail]');
    if (rail && active && rail.scrollWidth > rail.clientWidth) {
      const target = active.offsetLeft - rail.offsetLeft - (rail.clientWidth - active.offsetWidth) / 2;
      const left = Math.min(Math.max(target, 0), rail.scrollWidth - rail.clientWidth);
      try {
        if (typeof rail.scrollTo === 'function') rail.scrollTo({ left, behavior: 'smooth' });
        else rail.scrollLeft = left;
      } catch {
        rail.scrollLeft = left;
      }
    }
  }

  /* ---------------- the choice surface ---------------- */

  openSheet() {
    const sheet = this.querySelector('[data-fp-sheet]');
    if (this.dataset.pdpLayout === 'catalogue') {
      sheet?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.render();
      return;
    }
    if (sheet && !sheet.open) sheet.showModal();
    this.render();
  }

  closeSheet() {
    if (this.dataset.pdpLayout === 'catalogue') return;
    const sheet = this.querySelector('[data-fp-sheet]');
    if (sheet && sheet.open) sheet.close();
  }

  /** @param {string} id - 'swatch' | 'sample' | 'bulk' */
  pick(id) {
    this.#form = String(id);
    this.render();
  }

  /** @param {string|number} n */
  setColour(n) {
    const colour = this.#colourList().find((c) => String(c.n) === String(n));
    if (!colour || (!colour.available && !this.#isOther(colour))) return;
    this.#colour = String(colour.n);
    this.#form = 'sample';
    this.render();
  }

  /**
   * The colour number is read back from the escaped `data-fp-chip` attribute
   * rather than carried in the declarative action path, so no option value can
   * ever change which argument the handler receives.
   * @param {Event} event
   */
  setColourFromEvent(event) {
    const chip = event.target instanceof Element ? event.target.closest('[data-fp-chip]') : null;
    if (chip) this.setColour(chip.dataset.fpChip);
  }

  /**
   * The radios of the drawer's colour radiogroup, in document order. Scoped to
   * `.fp-chips` on purpose: the colour wall below the fold carries the same
   * `data-fp-chip` attribute and is NOT part of this group.
   * @returns {HTMLButtonElement[]}
   */
  #chipRadios() {
    return Array.from(this.querySelectorAll('.fp-chips [data-fp-chip]'));
  }

  /**
   * Radio-group keyboard behaviour (narrow review r1, BLOCKER 1).
   *
   * A radiogroup is one tab stop; inside it the arrow keys move AND select, and
   * they wrap. Home and End go to the ends, Space and Enter select in place.
   * Disabled colours are skipped rather than focused — a disabled button cannot
   * take focus, so stopping on one would strand the shopper.
   *
   * `setColour()` does the selecting, so the keyboard path and the click path
   * arm the form through exactly the same code, and the render it triggers is
   * what moves the roving `tabindex`. Other is the last radio and participates
   * like any other. Nothing here can reach the colour card or Bulk: those are
   * separate elements outside this group, and a key pressed anywhere but on a
   * member of this group returns immediately.
   *
   * @param {KeyboardEvent} event
   */
  handleChipKeys(event) {
    const radios = this.#chipRadios();
    if (!radios.length) return;
    const current = event.target instanceof Element ? event.target.closest('[data-fp-chip]') : null;
    if (!current || radios.indexOf(current) === -1) return;

    const usable = radios.filter((chip) => !chip.disabled);
    if (!usable.length) return;
    // If focus somehow sits on a disabled radio, treat the walk as starting
    // before the first usable one rather than refusing to move at all.
    const here = usable.indexOf(current);
    const move = (delta) => usable[(((here === -1 ? 0 : here + delta) % usable.length) + usable.length) % usable.length];

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = move(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = move(-1);
        break;
      case 'Home':
        next = usable[0];
        break;
      case 'End':
        next = usable[usable.length - 1];
        break;
      case ' ':
      case 'Spacebar':
      case 'Enter':
        // Select where focus already is. `preventDefault` below is what stops
        // the button's own activation from selecting it a second time.
        next = current.disabled ? null : current;
        break;
      default:
        return;
    }
    if (!next) return;
    event.preventDefault();
    this.setColour(next.dataset.fpChip);
    next.focus();
  }

  /** @param {Event} event */
  pickColourFromEvent(event) {
    const chip = event.target instanceof Element ? event.target.closest('[data-fp-chip]') : null;
    if (chip) this.pickColourAndOpen(chip.dataset.fpChip);
  }

  /**
   * The colour wall below the fold is a shortcut into the same surface: choose
   * that colour, put the visitor back at the top, open the drawer.
   * @param {string|number} n
   */
  pickColourAndOpen(n) {
    this.setColour(n);
    if (String(this.#colour) !== String(n)) return;
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.openSheet();
  }

  /** @param {number} delta */
  stepQty(delta) {
    const max = Number(this.#data.maxQty) || 99;
    this.#qty = Math.min(max, Math.max(1, this.#qty + Number(delta)));
    this.render();
  }

  #ready() {
    if (this.#form === 'bulk') return false;
    if (this.#form === 'swatch') return Boolean(this.#data.swatch && this.#data.swatch.available);
    const colour = this.#currentColour();
    if (!colour || !colour.available) return false;
    // Other has no confirmed ERP colour. A description starts an enquiry,
    // never a paid metre order, even if a stale variant still reports stock.
    if (this.#isOther(colour)) return false;
    return true;
  }

  #unitCents() {
    if (this.#form === 'swatch') return this.#data.swatch ? this.#data.swatch.price : 0;
    const colour = this.#currentColour();
    return colour ? colour.price : 0;
  }

  #lowestSampleCents() {
    const prices = this.#colourList().filter((c) => c.available && !this.#isOther(c)).map((c) => c.price);
    return prices.length ? Math.min(...prices) : 0;
  }

  render() {
    const strings = this.#data.strings || {};
    const isBulk = this.#form === 'bulk';
    const isSample = this.#form === 'sample';
    const colour = this.#currentColour();
    // Declared here, above every read. Round 3 finding 1: this `const` used to
    // sit two thirds of the way down the method while the first read was near
    // the top, so every call threw a ReferenceError out of the temporal dead
    // zone. The option cards and chips were updated before the throw and the
    // hidden id/quantity inputs after it, which meant a visitor could select
    // Sample, colour 2, 3 metres, see all of that reflected, and still submit
    // the colour card at quantity 1. `node --check` cannot see this; the
    // runtime harness now can.
    const pricesShown = this.dataset.pdpLayout === 'catalogue'
      ? this.#data.pricingState === 'approved'
      : this.#data.pricingState !== 'unknown';

    for (const option of this.querySelectorAll('[data-fp-opt]')) {
      const selected = option.dataset.fpOpt === this.#form;
      option.setAttribute('data-selected', String(selected));
      const hit = option.querySelector('[data-fp-opt-hit]');
      if (hit) hit.setAttribute('aria-expanded', String(selected));
    }

    // Narrow review r1, BLOCKER 2: this loop used to select EVERY `[data-fp-chip]`
    // on the page, which includes the colour-wall tiles below the fold. Those are
    // ordinary buttons, not radios, and `aria-checked` on a plain button is
    // invalid state that a screen reader either ignores or misreports. The radios
    // are the ones inside the drawer's radiogroup, and that is the whole of what
    // this loop is allowed to touch; the wall keeps the semantics it always had.
    //
    // BLOCKER 1: one tab stop, not one per colour. The chosen radio is the
    // tabbable one; with nothing chosen it is the first AVAILABLE radio, because
    // a disabled button cannot hold focus and a group whose only tab stop is
    // disabled has no tab stop at all.
    const radios = this.#chipRadios();
    const rovingTarget =
      radios.find((chip) => chip.dataset.fpChip === String(this.#colour) && !chip.disabled)
      || radios.find((chip) => !chip.disabled)
      || null;
    for (const chip of radios) {
      chip.setAttribute('aria-checked', String(chip.dataset.fpChip === String(this.#colour)));
      chip.setAttribute('tabindex', chip === rovingTarget ? '0' : '-1');
    }

    // D34. ONE boolean drives both the reveal and the field's `disabled`, so the
    // field a shopper cannot see is never a field the cart receives, and a
    // `required` field can never be hidden-but-enabled (which would block every
    // add to cart on the page, silently).
    const otherOpen = isSample && this.#isOther(colour);
    const otherBlock = this.querySelector('[data-fp-other]');
    if (otherBlock) otherBlock.hidden = !otherOpen;
    const otherInput = this.#otherInput();
    if (otherInput) otherInput.disabled = !otherOpen;
    const inquiry = this.querySelector('[data-fp-other-inquiry]');
    if (inquiry && otherOpen) {
      const subject = `Colour enquiry — ${inquiry.dataset.article || ''}`;
      const body = [inquiry.dataset.productUrl || '', `Colour: ${this.#otherText()}`, `Requested metres: ${this.#qty}`].join('\n');
      inquiry.href = `mailto:${inquiry.dataset.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    const qtyOut = this.querySelector('[data-fp-qty-out]');
    if (qtyOut) qtyOut.textContent = String(this.#qty);
    const quantityLabel = this.querySelector('[data-fp-commerce-quantity]');
    if (quantityLabel) {
      quantityLabel.hidden = !isSample;
      quantityLabel.textContent = `${this.#qty} metre${this.#qty === 1 ? '' : 's'}`;
    }
    const commercePrice = this.querySelector('[data-fp-commerce-price]');
    if (commercePrice && pricesShown) {
      commercePrice.textContent = colour ? this.#money(colour.price) : `${strings.from || 'From'} ${this.#money(this.#lowestSampleCents())}`;
    }
    const dec = this.querySelector('[data-fp-qty-dec]');
    if (dec) dec.disabled = this.#qty <= 1;
    const inc = this.querySelector('[data-fp-qty-inc]');
    if (inc) inc.disabled = this.#qty >= (Number(this.#data.maxQty) || 99);

    const samplePrice = this.querySelector('[data-fp-price-sample]');
    if (samplePrice) {
      samplePrice.textContent = otherOpen ? 'By enquiry' : !pricesShown
        ? '—'
        : colour
          ? this.#money(colour.price)
          : this.#lowestSampleCents()
            ? `${strings.from || 'from'} ${this.#money(this.#lowestSampleCents())}`
            : '—';
    }

    const colourHead = this.querySelector('[data-fp-colour-head]');
    if (colourHead) {
      colourHead.textContent = colour
        ? this.#isOther(colour)
          ? `${strings.colour || 'Colour'} — ${strings.otherLabel || 'Other'}`
          : this.#isNamed(colour)
            ? `${strings.colour || 'Colour'} — ${colour.label || colour.n}`
            : `${strings.colour || 'Colour'} — ${strings.no || 'no.'} ${colour.label || colour.n}`
        : strings.colour || 'Colour';
    }

    const foot = this.querySelector('[data-fp-foot]');
    if (foot) {
      foot.setAttribute('data-mode', isBulk || otherOpen ? 'enquiry' : 'buy');
      // 2026-09-04: the metres stepper lives in this bar now, and metres are a
      // Sample idea — the colour card is one piece and Bulk buys nothing. The
      // attribute is what the stylesheet displays it off, so the rule is stated
      // once and the first paint (server-rendered from `default_form`) already
      // agrees with it.
      foot.setAttribute('data-tier', this.#form);
    }

    const total = this.querySelector('[data-fp-total-value]');
    if (total) {
      total.textContent =
        pricesShown && this.#ready() ? this.#money(this.#unitCents() * (isSample ? this.#qty : 1)) : '—';
    }

    const addButton = this.querySelector('[data-fp-add] button');
    if (addButton) {
      addButton.disabled = !pricesShown || !this.#ready();
      const label = addButton.querySelector('[data-fp-add-label]');
      const text = otherOpen ? 'Ask about this colour' : !pricesShown
        ? strings.pricingUnavailable || 'Pricing unavailable'
        : isSample && !colour
          ? strings.chooseColour || 'Choose a colour'
          : // D34: Other chosen with nothing written is not "Unavailable" — the
            // variant is perfectly available, it is the page that still needs a
            // line from the shopper, and the button says which.
            this.#ready()
              ? strings.addToCart || 'Add to cart'
              : strings.unavailable || 'Unavailable';
      if (label) label.textContent = text;
      // Cold review r1, HIGH 1. A disabled button is not focusable, so the label
      // above can be true and still never reach a screen reader. `aria-disabled`
      // puts the state in the accessibility tree, and the live region below says
      // what would make the button work — announced when it changes, which is
      // when the shopper needs it.
      addButton.setAttribute('aria-disabled', String(addButton.disabled));
      const hint = this.querySelector('[data-fp-add-hint]');
      if (hint) {
        const hintText = !pricesShown
          ? strings.hintPricing || ''
          : isSample && !colour
            ? strings.hintChooseColour || ''
            : otherOpen && !this.#otherText()
              ? strings.hintDescribeColour || ''
              : '';
        // Only on a change: re-writing the same text into a live region makes a
        // screen reader repeat it on every unrelated render.
        if (hint.textContent.trim() !== hintText) hint.textContent = hintText;
      }
    }

    const variantInput = this.querySelector('[data-fp-variant-id]');
    if (variantInput) {
      // ONE invariant, and it is the same one the button is disabled by: the
      // form carries a variant id only in a state this page would actually let
      // someone submit. Everything else — bulk, an unpriced product, a sample
      // with no colour chosen, an unavailable variant — leaves it empty.
      //
      // Two separate findings live in this guard.
      //
      // The first: a ternary here used to fall through to the colour card's id
      // whenever the form was not Sample, so merely reading the bulk panel left
      // the colour card armed. Bulk is an enquiry, not a purchase.
      //
      // The second (closure review round 1, blocker 2): the assignment was not
      // gated by `pricesShown`. With pricing `unknown` the button was correctly
      // disabled, but the hidden input still held a real variant id — a disabled
      // button is a UI state, not a guarantee, and anything that submitted the
      // form by another route would have bought a variant whose price we had
      // just told the visitor we could not confirm. Withholding the figures and
      // still arming the purchase is exactly the failure the tri-state exists to
      // prevent.
      let id = '';
      if (pricesShown && this.#ready()) {
        if (isSample) id = colour ? colour.id : '';
        else if (this.#form === 'swatch' && this.#data.swatch) id = this.#data.swatch.id;
      }
      variantInput.value = id ? String(id) : '';
    }

    const quantityInput = this.querySelector('[data-fp-quantity]');
    if (quantityInput) quantityInput.value = String(isSample ? this.#qty : 1);

    const fine = this.querySelector('[data-fp-fine]');
    if (fine) {
      if (isBulk) {
        fine.textContent = strings.fineBulk || '';
      } else if (otherOpen) {
        fine.textContent = 'Contact us to confirm the colour before ordering.';
      } else if (isSample && colour) {
        fine.textContent = (strings.fineSample || '')
          .replace('{colour}', this.#isOther(colour) ? strings.otherLabel || 'Other' : String(colour.label || colour.n))
          .replace('{qty}', String(this.#qty));
      } else {
        fine.textContent = strings.fineDefault || '';
      }
    }
  }
}

if (!customElements.get('fivepoint-pdp')) {
  customElements.define('fivepoint-pdp', FivepointPdp);
}
